import React, { useState } from "react";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useLanguage } from "@/context/LanguageContext";
import { downloadDocument } from "@/lib/apiClient";
import { useAdminData } from "@/hooks/useAdminData";
import { StatsPanel } from "@/components/admin/StatsPanel";
import { LeadsTable } from "@/components/admin/LeadsTable";
import { DocumentsTable } from "@/components/admin/DocumentsTable";
import { LeadDetailsDialog } from "@/components/admin/LeadDetailsDialog";

export default function AdminDashboard() {
  const { t } = useLanguage();
  const { stats, leads, docs, loading, loadAll, logout, leadName, updateLead } = useAdminData();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  // Derive from the list so the dialog always reflects the latest saved values.
  const selectedLead = leads.find((l) => l.id === selectedLeadId) || null;

  const changeStatusFilter = (next) => {
    setStatusFilter(next);
    loadAll(search, next);
  };

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc.id, doc.original_filename);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else toast.error("Download failed");
    }
  };

  return (
    <div className="min-h-screen bg-[var(--brand-offwhite)]">
      <header className="sticky top-0 z-30 border-b border-[color:var(--border-subtle)] bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/assets/nina-mark.png" alt="Nina" className="h-9 w-9 object-contain" />
            <div>
              <div className="font-heading text-lg font-bold leading-none text-[var(--brand-navy)]">{t.admin.dashboardTitle}</div>
              <div className="text-[11px] text-[var(--muted-ink)]">Nina Latinos Multi Services</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => loadAll(search, statusFilter)} className="h-9 rounded-lg" data-testid="admin-refresh">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button onClick={logout} variant="outline" size="sm" className="h-9 rounded-lg font-semibold" data-testid="admin-logout">
              <LogOut className="mr-1.5 h-4 w-4" /> {t.admin.logout}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <StatsPanel stats={stats} activeStatus={statusFilter} onStatusClick={changeStatusFilter} />

        <Tabs defaultValue="leads" className="mt-8">
          <TabsList className="rounded-xl bg-[var(--surface-2)] p-1">
            <TabsTrigger value="leads" className="rounded-lg px-4 data-[state=active]:bg-white data-[state=active]:text-[var(--brand-burgundy)] data-[state=active]:shadow-sm" data-testid="tab-leads">{t.admin.tabLeads}</TabsTrigger>
            <TabsTrigger value="docs" className="rounded-lg px-4 data-[state=active]:bg-white data-[state=active]:text-[var(--brand-burgundy)] data-[state=active]:shadow-sm" data-testid="tab-docs">{t.admin.tabDocs}</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="mt-5">
            <LeadsTable
              leads={leads}
              search={search}
              onSearchChange={setSearch}
              onSearchSubmit={() => loadAll(search, statusFilter)}
              statusFilter={statusFilter}
              onStatusFilterChange={changeStatusFilter}
              onView={(lead) => setSelectedLeadId(lead.id)}
            />
          </TabsContent>

          <TabsContent value="docs" className="mt-5">
            <DocumentsTable docs={docs} leadName={leadName} onDownload={handleDownload} />
          </TabsContent>
        </Tabs>
      </main>

      <LeadDetailsDialog lead={selectedLead} onClose={() => setSelectedLeadId(null)} onSave={updateLead} />
    </div>
  );
}
